'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TrackframeHeaderLogo, TrackframeHeaderRoot, } from './primitives.js';
import { TrackframeHeaderUserMenu } from './user-menu.js';
import { TrackframeHeaderTrackPicker } from './track-picker.js';
import { useHeaderData } from './use-header-data.js';
import { signOut } from './sign-out.js';
import { portalLinks } from './portal-links.js';
/**
 * Shared application header. Logo on the left, optional `children` in the
 * center, two dropdown menus on the right: the track picker (Dashboard,
 * navigable tracks, optional Admin) and the user menu (avatar → identity +
 * sign-out).
 *
 * Pulls identity from `/auth/me`, branding from `/settings/branding`, and
 * navigable tracks from `/tracks`. Logo bytes load from `/settings/logo`
 * (cross-origin `<img>` works without preflight; the auth hook bypasses that
 * route so unauthenticated `<img>` requests succeed for branding).
 *
 * Two deployment modes:
 *
 *   - Same-origin (hub admin SPA, hub root):
 *       <TrackframeHeader hubBaseUrl={hubBaseUrl} />
 *
 *   - Track frontend (different origin from the hub):
 *       <TrackframeHeader
 *         hubBaseUrl="https://hub.example.com"
 *         hubProxyBaseUrl="/hub"
 *         getAuthHeaders={() => ({ Authorization: `Bearer ${jwt}` })}
 *       >
 *         {breadcrumbs}
 *       </TrackframeHeader>
 */
export function TrackframeHeader({ hubBaseUrl, hubProxyBaseUrl, getAuthHeaders, onLogoClick, signOut: signOutOpts, children, brandFallback, showTrackPicker = true, showUserMenu = true, userMenuItems, className, }) {
    const { identity, branding, tracks } = useHeaderData({
        hubBaseUrl,
        hubProxyBaseUrl,
        getAuthHeaders,
    });
    const logoSrc = branding?.logoUrl
        ? hubBaseUrl
            ? `${hubBaseUrl.replace(/\/+$/, '')}${branding.logoUrl}`
            : branding.logoUrl
        : null;
    const handleSignOut = () => {
        void signOut({ hubBaseUrl, ...signOutOpts });
    };
    // Cross-app navigation targets the portal origin the hub reports
    // (`APP_BASE_URL` via /settings/branding) — never derived from `hubBaseUrl`.
    const { portalHref, adminHref } = portalLinks({
        appBaseUrl: branding?.appBaseUrl,
        isAdmin: identity?.roles?.includes('admin') ?? false,
    });
    const logoNode = (_jsx(TrackframeHeaderLogo, { src: logoSrc, fallback: brandFallback, alt: "Trackframe" }));
    return (_jsxs(TrackframeHeaderRoot, { className: className, children: [onLogoClick ? (_jsx("button", { type: "button", className: "tf-header-logo-button", onClick: onLogoClick, "aria-label": "Home", children: logoNode })) : (logoNode), _jsx("div", { className: "tf-header-center", children: children }), _jsxs("div", { className: "tf-header-actions", children: [showTrackPicker && (_jsx(TrackframeHeaderTrackPicker, { tracks: tracks, portalHref: portalHref, adminHref: adminHref })), showUserMenu && (_jsx(TrackframeHeaderUserMenu, { email: identity?.email ?? null, name: identity?.name ?? null, avatarUrl: identity?.avatarUrl ?? null, onSignOut: handleSignOut, children: userMenuItems }))] })] }));
}
//# sourceMappingURL=header.js.map